import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  Avatar,
  CircularProgress,
  Container,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axiosInstance from "../../api/axiosInstance";
import { BASE_API_URL } from "../../constants";
import signinImage from "../../assets/img/abanotes3.png"; 

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [redirecting, setRedirecting] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [verificationFailed, setVerificationFailed] = useState(false);

  const navigate = useNavigate();

  // ✅ Apply Background Image
  useEffect(() => {
    document.body.style.backgroundImage = `url(${signinImage})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundPosition = "center";

    return () => {
      document.body.style.backgroundImage = "";
    };
  }, []);

  // ✅ Extract token from URL and verify
  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      axiosInstance
        .get(`${BASE_API_URL}/reset-password/verify?token=${tokenFromUrl}`)
        .then((res) => {
          setEmail(res.data.email);
          setToken(res.data.token);
        })
        .catch(() => {
          setVerificationFailed(true);
        });
    }
  }, [searchParams]);

  // ✅ Validate password strength
  const validatePassword = (value) => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{5,}$/;
    if (!regex.test(value)) {
      setPasswordError("Must include lowercase, uppercase, number & special character.");
    } else {
      setPasswordError("");
    }
  };

  // ✅ Handle password reset
  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: "Passwords do not match!", severity: "error" });
      return;
    }

    if (passwordError) {
      setSnackbar({ open: true, message: passwordError, severity: "error" });
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post(`${BASE_API_URL}/resetpassword/confirm`, {
        token,
        newPassword,
      });

      if (res.status === 200) {
        setSnackbar({ open: true, message: "Password updated successfully!", severity: "success" });
        localStorage.clear();
        setRedirecting(true);

        let countdownTimer = 3;
        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev === 1) {
              clearInterval(interval);
              localStorage.clear();
              navigate("/login"); // ✅ Redirect to login page
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setSnackbar({ open: true, message: "Failed to reset password.", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };
  if (verificationFailed) {
    return (
      <Container
        sx={{
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          backdropFilter: "blur(10px)",
          borderRadius: "10px",
          border: "1px solid rgba(255, 255, 255, 0.18)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Invalid or Expired Link
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          The password reset link is invalid or has expired. Please request a new link.
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate("/password-reset-options")}>
          Request New Link
        </Button>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        height: "80vh",
      width: { xs: "12", md: "12", xl: "12" },
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backdropFilter: "blur(10px)",
      borderRadius: "10px",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      }}
    >
      {/* Left Side - Title */}
      <Box
        sx={{
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "left",
        }}
      >
        <Typography  
        className="login-message"
              variant="h2"
              gutterBottom
              sx={{ ml: 10 }}>
          Enter
        </Typography>

        <Typography className="login-message"
              variant="h2"
              gutterBottom
              sx={{
                ml: 10,
              }}>
          Your Password
        </Typography>
      </Box>

      {/* Right Side - Form */}
      <Box
      className="login-container"
        component="form"
        sx={{
          mt: 4,
          mb: 4,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          marginLeft: "auto",
          marginRight: "20px",
          width: "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: "auto", bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Reset Your Password
        </Typography>

        <TextField fullWidth label="Email" value={email} disabled sx={{ mb: 2 }} />

        <TextField
          fullWidth
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => {
            setNewPassword(e.target.value);
            validatePassword(e.target.value);
          }}
          error={!!passwordError}
          helperText={passwordError}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={newPassword !== confirmPassword && confirmPassword !== ""}
          helperText={newPassword !== confirmPassword && confirmPassword !== "" ? "Passwords do not match" : ""}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          onClick={handleResetPassword}
          fullWidth
          sx={{
            backgroundColor: "#e9a132",
            "&:hover": { backgroundColor: "#d8902e" },
          }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Update Password"}
        </Button>
      </Box>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ResetPassword;
